import { addDays, measurementType, monthOf, prepareReading, targetFor, type DayPoint, type MeasurementErrorCode, type Target } from '@gk/contracts'
import { UnitError } from '@gk/contracts'
import { localDate, localToInstant } from '../lib/time.ts'

/** Pure logic of the measurement screens (no React, no clock): the chart's geometry, the form's own checks, and the reading of a file from an instrument. Tested in measureUi.test.ts. */

// ---- the chart ----------------------------------------------------------------------------------------------------------------------------
export interface ChartPoint extends DayPoint { x: number; y: number; yMin: number; yMax: number }
export interface Band { x1: number; x2: number; yLow: number | null; yHigh: number | null; low: number | null; high: number | null }
export interface ChartModel {
  width: number; height: number
  points: ChartPoint[]
  /** One polyline over the day means (a break where a gap is longer than `gapDays`). */
  segments: string[]
  bands: Band[]
  yTicks: Array<{ y: number; label: string }>
  xTicks: Array<{ x: number; label: string }>
  domain: { min: number; max: number }
  /** The same scale as the points, for anything else drawn on the chart (a rolling mean, a marker). */
  xOf(date: string): number
  yOf(value: number): number
}
const PAD = { l: 44, r: 12, t: 12, b: 28 }
const dayNo = (iso: string): number => Math.round(Date.parse(`${iso}T00:00:00Z`) / 86_400_000)
const niceStep = (span: number): number => { const raw = span / 4; const p = 10 ** Math.floor(Math.log10(raw)); const f = raw / p; return (f < 1.5 ? 1 : f < 3.5 ? 2 : f < 7.5 ? 5 : 10) * p }

/** The geometry of one timeline: day means as a line with their min-max whiskers, and the club's target band behind it (one rectangle per run of days under the same target). `targetOn(date)` gives the target
 *  that applies on a day (or none). Nothing is smoothed, interpolated or guessed: a day with no reading is a gap. */
export function chartModel(points: readonly DayPoint[], targetOn: (date: string) => Pick<Target, 'low' | 'high'> | undefined, o: { width?: number; height?: number; gapDays?: number; decimals?: number } = {}): ChartModel {
  const width = o.width ?? 640, height = o.height ?? 260, gapDays = o.gapDays ?? 3, decimals = o.decimals ?? 1
  if (points.length === 0) return { width, height, points: [], segments: [], bands: [], yTicks: [], xTicks: [], domain: { min: 0, max: 1 }, xOf: () => PAD.l, yOf: () => PAD.t }
  const first = dayNo(points[0]!.date), last = dayNo(points[points.length - 1]!.date)
  const span = Math.max(last - first, 1)
  // the target's bounds on the days of the series count towards the scale: a band you cannot see is no use
  const days: string[] = []
  for (let d = first; d <= last && days.length < 1200; d++) days.push(addDays('1970-01-01', d))
  const bounds: number[] = []
  const perDay = days.map((d) => { const t = targetOn(d); if (t?.low !== null && t?.low !== undefined) bounds.push(t.low); if (t?.high !== null && t?.high !== undefined) bounds.push(t.high); return t })
  const values = [...points.flatMap((p) => [p.min, p.max]), ...bounds]
  let lo = Math.min(...values), hi = Math.max(...values)
  if (hi - lo < 1e-9) { lo -= 1; hi += 1 }
  const margin = (hi - lo) * 0.08
  lo -= margin; hi += margin
  const step = niceStep(hi - lo)
  const min = Math.floor(lo / step) * step, max = Math.ceil(hi / step) * step
  const plotW = width - PAD.l - PAD.r, plotH = height - PAD.t - PAD.b
  const xOf = (dn: number) => PAD.l + ((dn - first) / span) * plotW
  const yOf = (v: number) => PAD.t + (1 - (v - min) / (max - min)) * plotH
  const pts: ChartPoint[] = points.map((p) => ({ ...p, x: xOf(dayNo(p.date)), y: yOf(p.mean), yMin: yOf(p.min), yMax: yOf(p.max) }))
  const segments: string[] = []; let cur: string[] = []; let prev = -Infinity
  for (const p of pts) {
    const dn = dayNo(p.date)
    if (cur.length && dn - prev > gapDays) { segments.push(cur.join(' ')); cur = [] }
    cur.push(`${cur.length ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`); prev = dn
  }
  if (cur.length) segments.push(cur.join(' '))
  const bands: Band[] = []
  let run: { from: number; to: number; low: number | null; high: number | null } | null = null
  const flush = () => { if (run) bands.push({ x1: xOf(run.from), x2: xOf(run.to + 1 > last ? last : run.to + 1), yLow: run.low === null ? null : yOf(run.low), yHigh: run.high === null ? null : yOf(run.high), low: run.low, high: run.high }); run = null }
  perDay.forEach((t, i) => {
    const dn = first + i
    if (!t) { flush(); return }
    if (run && run.low === t.low && run.high === t.high) run.to = dn
    else { flush(); run = { from: dn, to: dn, low: t.low, high: t.high } }
  })
  flush()
  const yTicks: ChartModel['yTicks'] = []
  for (let v = min; v <= max + step / 2; v += step) yTicks.push({ y: yOf(v), label: Number(v.toFixed(decimals)).toString() })
  const xTicks: ChartModel['xTicks'] = []
  const every = Math.max(1, Math.ceil(span / 5))
  for (let dn = first; dn <= last; dn += every) xTicks.push({ x: xOf(dn), label: addDays('1970-01-01', dn).slice(5).split('-').reverse().join('/') })
  return { width, height, points: pts, segments, bands, yTicks, xTicks, domain: { min, max }, xOf: (date) => xOf(dayNo(date)), yOf }
}

// ---- the form's own check (the twin of the server's, in the person's words) ---------------------------------------------------------------
export interface FormCheck { ok: boolean; code?: MeasurementErrorCode; detail?: string; canonical?: string }
export function checkForm(f: { type: string; value: string; unit: string; hasArea: boolean; depth: string }): FormCheck {
  if (f.value.trim() === '') return { ok: false }
  const depth = f.depth.trim() === '' ? null : Number(f.depth)
  try {
    const r = prepareReading({ type: f.type, value: f.value, unit: f.unit, hasArea: f.hasArea, depthMm: depth !== null && Number.isNaN(depth) ? -1 : depth })
    return { ok: true, canonical: r.value }
  } catch (e) {
    if (e instanceof UnitError) return { ok: false, code: e.code, detail: e.detail }
    const c = (e as { code?: MeasurementErrorCode; detail?: string })
    return { ok: false, code: c.code, detail: c.detail }
  }
}

// ---- reading a file from an instrument (GK-039) --------------------------------------------------------------------------------------------
/** What the person decided about the columns of the file. Each value column becomes readings of one type, in one unit. */
export interface ImportMapping {
  date: string | null
  time: string | null
  /** A column holding an area code or name, or one fixed area for the whole file. */
  areaColumn: string | null
  fixedAreaId: string | null
  depthColumn: string | null
  instrumentColumn: string | null
  fixedInstrument: string
  method: string
  values: Array<{ column: string; type: string; unit: string }>
}
export interface ImportRow { measurement_type: string; area_id: string | null; value: number; unit: string; observed_at: string; depth_mm: number | null; method: string | null; instrument: string | null }
export interface ImportProblem { row: number; column: string | null; code: string; detail: string }
export interface AreaKey { id: string; code: string | null; names: string[] }

const clean = (v: string | undefined): string => (v ?? '').trim()
const norm = (v: string): string => v.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim()

/** A date and an optional time, in the shapes instruments and French Excel write, as an instant in the site's own zone (or exactly as written when it carries a Z or an offset). `null` when it is not a date. */
export function parseWhen(date: string, time: string | null, tz: string): string | null {
  const d = clean(date), t = clean(time ?? '')
  let m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(Z|[+-]\d{2}:?\d{2})?)?$/.exec(d)
  let day: string | null = null, hhmm: string | null = null
  if (m) {
    day = `${m[1]}-${m[2]}-${m[3]}`
    if (m[4]) {
      if (m[7]) { const iso = `${day}T${m[4]!.padStart(2, '0')}:${m[5]}:${m[6] ?? '00'}${m[7] === 'Z' ? 'Z' : m[7]!.replace(/^([+-]\d{2})(\d{2})$/, '$1:$2')}`; const ms = Date.parse(iso); return Number.isNaN(ms) ? null : new Date(ms).toISOString() }
      hhmm = `${m[4]!.padStart(2, '0')}:${m[5]}`
    }
  } else {
    m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:[ T](\d{1,2})[:h](\d{2})(?::\d{2})?)?$/.exec(d)
    if (!m) return null
    day = `${m[3]}-${m[2]!.padStart(2, '0')}-${m[1]!.padStart(2, '0')}`
    if (m[4]) hhmm = `${m[4]!.padStart(2, '0')}:${m[5]}`
  }
  if (!hhmm && t) {
    const tm = /^(\d{1,2})[:h](\d{2})(?::\d{2})?$/.exec(t)
    if (!tm) return null
    hhmm = `${tm[1]!.padStart(2, '0')}:${tm[2]}`
  }
  const [y, mo, dd] = day.split('-').map(Number) as [number, number, number]
  const probe = new Date(Date.UTC(y, mo - 1, dd))
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== dd) return null          // 31/02 is not a date
  const [hh, mi] = (hhmm ?? '12:00').split(':').map(Number) as [number, number]
  if (hh > 23 || mi > 59) return null
  return localToInstant(day, hhmm ?? '12:00', tz)                           // a date with no time is read at noon: the day is what matters, never a made-up hour
}

/** The rows of the file, checked the way the server checks each one. `problems` names the row (1 = the first data row), the column and the code; a file with any problem is not sent. */
export function buildImportRows(table: { headers: string[]; rows: string[][] }, map: ImportMapping, areas: readonly AreaKey[], tz: string, now: Date): { rows: ImportRow[]; problems: ImportProblem[] } {
  const col = (name: string | null): number => (name ? table.headers.indexOf(name) : -1)
  const di = col(map.date), ti = col(map.time), ai = col(map.areaColumn), pi = col(map.depthColumn), ii = col(map.instrumentColumn)
  const byKey = new Map<string, string[]>()
  for (const a of areas) for (const k of [a.code ?? '', ...a.names].map(norm).filter(Boolean)) byKey.set(k, [...(byKey.get(k) ?? []), a.id])
  const rows: ImportRow[] = []; const problems: ImportProblem[] = []
  const bad = (row: number, column: string | null, code: string, detail = '') => problems.push({ row, column, code, detail })
  if (di < 0) { bad(0, null, 'IMPORT_NO_DATE'); return { rows, problems } }
  if (map.values.length === 0) { bad(0, null, 'IMPORT_NO_VALUE'); return { rows, problems } }
  table.rows.forEach((r, n) => {
    const row = n + 1
    const when = parseWhen(r[di] ?? '', ti >= 0 ? (r[ti] ?? '') : null, tz)
    if (!when) { bad(row, map.date, 'IMPORT_DATE_INVALID', clean(r[di])); return }
    let areaId: string | null = map.fixedAreaId
    if (ai >= 0) {
      const raw = clean(r[ai])
      if (raw === '') areaId = map.fixedAreaId
      else { const hit = byKey.get(norm(raw)); if (!hit) { bad(row, map.areaColumn, 'IMPORT_AREA_UNKNOWN', raw); return } if (hit.length > 1) { bad(row, map.areaColumn, 'IMPORT_AREA_AMBIGUOUS', raw); return } areaId = hit[0]! }
    }
    const depthRaw = pi >= 0 ? clean(r[pi]) : ''
    const depth = depthRaw === '' ? null : Number(depthRaw.replace(',', '.'))
    const instrument = (ii >= 0 ? clean(r[ii]) : '') || clean(map.fixedInstrument) || null
    for (const v of map.values) {
      const vi = col(v.column)
      const raw = clean(r[vi])
      if (raw === '') continue                                               // an empty cell is no reading
      const f = checkForm({ type: v.type, value: raw, unit: v.unit, hasArea: areaId !== null, depth: depth === null ? '' : String(depth) })
      if (!f.ok) { bad(row, v.column, f.code ?? 'MEASUREMENT_VALUE_INVALID', f.detail ?? raw); continue }
      if (new Date(when).getTime() > now.getTime() + 86_400_000) { bad(row, map.date, 'MEASUREMENT_DATE_INVALID', clean(r[di])); continue }
      rows.push({ measurement_type: v.type, area_id: areaId, value: Number(raw.replace(',', '.')), unit: v.unit, observed_at: when, depth_mm: depth === null || Number.isNaN(depth) ? null : depth, method: clean(map.method) || null, instrument })
    }
  })
  if (rows.length === 0 && problems.length === 0) bad(0, null, 'IMPORT_NO_ROWS')
  return { rows, problems }
}

/** The file's rows in chunks the server accepts (a thousand a command), each with its own identity: the file's hash for a single chunk, otherwise a hash of the file's and the chunk's place. */
export const IMPORT_CHUNK = 1000
export function chunkRows<T>(rows: readonly T[], size = IMPORT_CHUNK): T[][] {
  const out: T[][] = []
  for (let i = 0; i < rows.length; i += size) out.push(rows.slice(i, i + size))
  return out
}

/** A one-line summary of a day's judgement for the list: the latest daily mean against the target of its month. */
export function latestVerdict(points: readonly DayPoint[], targets: readonly Target[], type: string, areaType: string | null): { date: string; mean: number; target: Target | undefined } | null {
  const p = points[points.length - 1]
  if (!p) return null
  return { date: p.date, mean: p.mean, target: targetFor(targets, type, areaType, monthOf(p.date)) }
}
export const typeDef = measurementType

// ---- what was done on the area: the markers on the timeline ----------------------------------------------------------------------------------
export interface MaintenanceEvent { date: string; taskCode: string }
/** The work that covered this area (a completion's coverage names the areas it covered), one marker per day and kind of task, in the club's own days: mowing, aerating, top-dressing, whatever the crew finished. A reading
 *  and a treatment of the same day line up; nothing here claims the one caused the other. */
export function maintenanceEvents(areaId: string, completions: ReadonlyArray<{ id: string; job_id: string; completed_at: string }>, coverage: ReadonlyArray<{ completion_id: string; area_id: string }>,
  jobs: ReadonlyArray<{ id: string; task_type_code: string }>, tz: string): MaintenanceEvent[] {
  const covered = new Set(coverage.filter((c) => c.area_id === areaId).map((c) => c.completion_id))
  const jobCode = new Map(jobs.map((j) => [j.id, j.task_type_code]))
  const seen = new Set<string>(); const out: MaintenanceEvent[] = []
  for (const c of completions) {
    if (!covered.has(c.id)) continue
    const code = jobCode.get(c.job_id); if (!code) continue
    const date = localDate(c.completed_at, tz)
    const key = `${date}|${code}`
    if (!seen.has(key)) { seen.add(key); out.push({ date, taskCode: code }) }
  }
  return out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.taskCode < b.taskCode ? -1 : 1))
}

// ---- what was irrigated on the area: the water-drop markers on the soil-moisture series (M16-M) ----------------------------------------------------------
export interface IrrigationEvent { date: string; kind: 'program' | 'manual' | 'test'; /** The sum of the depths given that day, or null when no run of the day carried one. */ depthMm: number | null; runs: number }
/** The runs recorded on this area, one marker per club day and kind (a corrected run is replaced by its correction, which is also a row), oldest first. Like the maintenance markers, a marker beside a
 *  reading says the two happened; it never says the one caused the other (the response is `runResponse`). */
export function irrigationEvents(areaId: string, runs: ReadonlyArray<{ area_id: string; started_local_date: string; kind: 'program' | 'manual' | 'test'; depth_mm: number | null; superseded_by_id?: string | null }>): IrrigationEvent[] {
  const by = new Map<string, IrrigationEvent>()
  for (const r of runs) {
    if (r.area_id !== areaId || r.superseded_by_id) continue
    const key = `${r.started_local_date}|${r.kind}`
    const e = by.get(key) ?? { date: r.started_local_date, kind: r.kind, depthMm: null, runs: 0 }
    e.runs++; if (r.depth_mm !== null) e.depthMm = (e.depthMm ?? 0) + r.depth_mm
    by.set(key, e)
  }
  const order = { program: 0, manual: 1, test: 2 }
  return [...by.values()].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : order[a.kind] - order[b.kind]))
}

// ---- suggesting the columns of a file (everything here is a suggestion the person confirms) ----------------------------------------------------
const VALUE_HINTS: Array<{ re: RegExp; type: string; unit: string }> = [
  { re: /temp.*max|max.*temp|^tmax|t max/, type: 'air_temperature_max', unit: 'degC' },
  { re: /temp.*min|min.*temp|^tmin|t min/, type: 'air_temperature_min', unit: 'degC' },
  { re: /temp.*(sol|soil)|(sol|soil).*temp/, type: 'soil_temperature', unit: 'degC' },
  { re: /humid.*(air|rh)|^rh$|hr air|relative humidity/, type: 'relative_humidity', unit: 'pct' },
  { re: /humid|moist|vwc|teneur en eau/, type: 'soil_moisture', unit: 'pct' },
  { re: /temp.*air|air.*temp|^temp/, type: 'air_temperature', unit: 'degC' },
  { re: /pluie|rain|precip/, type: 'rainfall', unit: 'mm' },
  { re: /stimp|vitesse|speed/, type: 'green_speed', unit: 'ft' },
  { re: /ferm|clegg|gmax|firm/, type: 'firmness', unit: 'gmax' },
  { re: /feutre|thatch/, type: 'thatch_depth', unit: 'mm' },
  { re: /\bph\b/, type: 'soil_ph', unit: 'ph' },
  { re: /\bec\b|conduct/, type: 'soil_ec', unit: 'dS_m' },
  { re: /tonte|clipping|clippings/, type: 'clipping_volume', unit: 'L' },
]
export function suggestImportMapping(headers: readonly string[]): ImportMapping {
  const n = headers.map(norm)
  const find = (re: RegExp, not: number[] = []): string | null => { const i = n.findIndex((h, k) => !not.includes(k) && re.test(h)); return i >= 0 ? headers[i]! : null }
  const date = find(/^date|jour|day|timestamp|horodat/)
  const time = find(/^heure|^time|^hour/, date ? [headers.indexOf(date)] : [])
  const areaColumn = find(/zone|green|area|emplacement|parcelle|code/)
  const depthColumn = find(/prof|depth/)
  const instrumentColumn = find(/capteur|sonde|instrument|appareil|device|sensor/)
  const taken = new Set([date, time, areaColumn, depthColumn, instrumentColumn].filter(Boolean) as string[])
  const values: ImportMapping['values'] = []
  headers.forEach((h, i) => {
    if (taken.has(h)) return
    const hit = VALUE_HINTS.find((v) => v.re.test(n[i]!))
    if (hit) values.push({ column: h, type: hit.type, unit: hit.unit })
  })
  return { date, time, areaColumn, fixedAreaId: null, depthColumn, instrumentColumn, fixedInstrument: '', method: '', values }
}
